/**
 * The two statements a client signs before submitting onboarding: the tax residence
 * self-certification and the declarations (own account, public positions, source of the money).
 *
 * Every line is printed by the server from the client's answers, in the language of the screen,
 * and the words signed come from the server too: what is shown is what is sealed. The client adds
 * only what the profile cannot hold (why a tax number is missing, a further country of tax
 * residence) and types their name.
 */
import type { AxiosInstance } from 'axios';
import authService from './auth.service';

export type StatementKind = 'crs_self_certification' | 'kyc_declarations';
export type StatementState = 'current' | 'stale' | 'missing';

export interface StatementLine {
  label: string;
  value: string;
}

export interface StatementView {
  kind: StatementKind;
  version: string;
  title: string;
  body: string[];
  lines: StatementLine[];
}

export interface NoTinReason {
  code: string;
  /** The reason in the language of the signature. */
  label: string;
}

export interface StatementsPreview {
  case_id: string;
  language: 'en' | 'es';
  applies: boolean;
  editable: boolean;
  acts_for_another: boolean;
  missing: Record<StatementKind, string[]>;
  statements: StatementView[];
  status: Record<StatementKind, StatementState>;
  countries_needing_a_reason: string[];
  max_extra_jurisdictions: number;
  no_tin_reasons: NoTinReason[];
  /** Why the tax residence cannot be signed as it stands (a country without its number or reason). */
  error: string | null;
  /** The hash of what is shown: sent back with the signature. */
  shown_sha256: string;
  step_up_required: boolean;
  legal_name: string;
  esign_template: string;
}

export interface ReasonInput {
  reason: string;
  explanation: string;
}

export interface ExtraInput {
  country: string;
  tin: string;
  reason: string;
  explanation: string;
}

/** What the person adds while signing. */
export interface TypedInput {
  language: 'en' | 'es';
  noTinReasons: Record<string, ReasonInput>;
  extras: ExtraInput[];
}

export interface SignStatementsInput extends TypedInput {
  signerName: string;
  mfaCode: string | null;
  shownSha256: string;
}

export interface SignedStatements {
  statement_ids: Record<StatementKind, string>;
  signed_on: string;
}

function typedBody(input: TypedInput): Record<string, unknown> {
  return {
    language: input.language,
    no_tin_reasons: input.noTinReasons,
    extras: input.extras.filter((e) => e.country !== ''),
  };
}

class KycStatementsService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async preview(caseId: string, input: TypedInput): Promise<StatementsPreview> {
    const res = await this.api.post<StatementsPreview>(
      `/api/v1/kyc-statements/${caseId}/preview`,
      typedBody(input),
    );
    return res.data;
  }

  async sign(caseId: string, input: SignStatementsInput): Promise<SignedStatements> {
    const res = await this.api.post<SignedStatements>(`/api/v1/kyc-statements/${caseId}/sign`, {
      ...typedBody(input),
      signer_name: input.signerName,
      mfa_code: input.mfaCode,
      shown_sha256: input.shownSha256,
      confirms_statements: true,
    });
    return res.data;
  }
}

export default new KycStatementsService();
